"use client";
import React, { useEffect, useState } from "react";
import Image from "next/image";
import "@/src/components/Home/LandingV6/landing-v6.css";
import { Nav, StartWritingButton } from "@/src/components/Home/LandingV6/ui";
import { Footer } from "@/src/components/Home/LandingV6/blocks";
import { CornerPin, Squiggle } from "@/src/components/Home/LandingV6/ornaments";

const FEATURES = [
  {
    id: "reports",
    kicker: "editorial reports · feature nº 01",
    title: "a verdict,",
    titleAccent: "not a compliment.",
    body: "pick a depth, from a quick editorial letter up to a full assessment, and quarkle reads the whole chapter or book in one sitting. a revision plan sorted into critical, recommended, and optional. every promise the book makes, tracked to where it pays off. a map of where the tension holds or sags.",
    img: "/images/app/feat-report.png",
    imgAlt: "The Full Assessment's Verdict tab: the verdict, a How It Reads readability sidebar, a chapter-by-chapter tension chart, and the plan in brief",
    accent: "var(--pink-deep)",
  },
  {
    kicker: "chat · feature nº 02",
    title: "ask it anything.",
    titleAccent: "it already knows.",
    body: "quarkle chat answers from your actual manuscript: characters, world, and plot notes it keeps updated as you write. no re-explaining your book every session.",
    img: "/images/app/editor-overview.png",
    imgAlt: "Quarkle Studio: the manuscript editor next to Quarkle Chat, answering a question about a character with details drawn from the book",
    accent: "var(--pink)",
  },
  {
    id: "chat-edits",
    kicker: "chat edits · feature nº 03",
    title: "an editor that",
    titleAccent: "can hold the pen.",
    body: "quarkle chat doesn't just advise. ask it to make the change and it edits the chapter for you, staged as a diff you can see: what it now reads, what apply would make it, added words highlighted, removed struck through. it never commits anything itself. apply it, skip it, or select part of the diff and keep only that.",
    img: "/images/app/feat-chat-edits.png",
    imgAlt: "Quarkle Chat asked to change a line's ending, with the edit staged as a diff card showing Now Reads next to Apply Would Make, with Hide, Skip, and Apply, and the same diff live in the manuscript",
    accent: "var(--sky)",
  },
  {
    id: "review-room",
    kicker: "the review room · feature nº 04",
    title: "twelve editors.",
    titleAccent: "one room.",
    body: "your chapter goes through 12 specialist AI review passes grouped into four editing jobs: copy, line, developmental, continuity. within every tier, an AI consolidation pass merges duplicate notes, resolves conflicts, and keeps the sharpest version of each point.",
    img: "/images/app/feat-review-room.png",
    imgAlt: "A manuscript with a live grammar suggestion popover open, next to the Review Room panel showing the Copy, Line, and Developmental reviewer tiers",
    accent: "var(--clay)",
  },
  {
    kicker: "the story atlas · feature nº 05",
    title: "your book,",
    titleAccent: "indexed.",
    body: "quarkle reads your manuscript and builds a set of notes from it (characters, objects, places, and chapter summaries), updating them as the draft changes. freeze a note to pin canon exactly as you wrote it, and review saved changes in version history.",
    img: "/images/app/feat-atlas-tree.png",
    imgAlt: "The Story Atlas ledger with System, Summaries, Chapter Notes, Character, and Objects sections, every file auto-discovered from the manuscript with word counts and last-updated times",
    accent: "var(--violet)",
    tall: true,
  },
  {
    kicker: "the story atlas · dossiers",
    title: "your cast,",
    titleAccent: "auto-profiled.",
    body: "open any character and there's a real dossier quarkle wrote from your chapters: role, personality, backstory, and the arc so far, kept current as the story moves. add a portrait from the Drawing Room and the note becomes a face. this dossier on Noa existed before the author ever asked for it.",
    img: "/images/app/feat-atlas-note.png",
    imgAlt: "An auto-written character dossier for Noa Kessler with an AI portrait: role, personality, backstory, and arc drawn from the manuscript",
    accent: "var(--pink)",
    tall: true,
  },
  {
    id: "drawing-room",
    kicker: "the drawing room · feature nº 06",
    title: "see your book,",
    titleAccent: "illustrated.",
    body: "create AI book covers, character portraits, and scene illustrations from the manuscript itself. gather a chapter or Story Atlas notes, review the exact image prompt, choose a style and shape, then keep only the takes that belong in your book.",
    img: "/images/app/feat-drawing-styles.jpg",
    imgAlt: "The Drawing Room's style shelf, grouped by family — book and poster, comics and screen, made by hand, photographic — with styles from storybook and art nouveau to manga, pixel art, stop-motion clay, paper collage, and embroidery, each previewing the same scene",
    accent: "var(--pink-deep)",
  },
  {
    id: "continue-writing",
    kicker: "continue writing · feature nº 07",
    title: "stuck mid-scene?",
    titleAccent: "pass the quill.",
    body: "press the quill and quarkle reads your book, then writes the next passage straight into the chapter, streamed word by word in ghost ink: a tint that marks every sentence it wrote until you claim it. steer it with a direction, set how far it runs, stop it mid-sentence and keep what landed. undoing the whole passage is one gesture.",
    video: "/images/app/feat-continue-writing.mp4",
    videoDims: [1198, 720],
    imgAlt: "The quill pressed at the end of a chapter: Quarkle writes the next passage into the manuscript in tinted ghost ink, word by word",
    accent: "var(--pink-deep)",
  },
  {
    id: "highlight-bar",
    kicker: "the highlight bar",
    title: "select a line.",
    titleAccent: "rewrite it in place.",
    body: "highlight any passage and pick expand, embellish, concise, or restructure. the result streams into a preview card as a diff: added words highlighted, removed words struck through. nothing changes in your manuscript until you accept it.",
    img: "/images/app/feat-highlight-bar.png",
    imgAlt: "The Highlight Bar over a selected sentence, with Expand, Embellish, Concise, and Restructure",
    accent: "var(--sky)",
  },
  {
    id: "version-history",
    kicker: "version history",
    title: "keep every draft",
    titleAccent: "within reach.",
    body: "quarkle saves a checkpoint roughly every 250 words, plus any you save yourself. compare any two checkpoints as a word-level diff, and restore an older one, with a fresh checkpoint of the draft you're leaving.",
    img: "/images/app/feat-versions-wide.png",
    imgAlt: "A before-and-after comparison between two checkpoints, with removed text struck through in red and added text highlighted in green",
    accent: "var(--moss)",
  },
  {
    id: "atlas-activity",
    kicker: "the story atlas · activity",
    title: "every note,",
    titleAccent: "versioned.",
    body: "your book's memory keeps its own revision log: who changed what, you or quarkle, down to the word, newest first. open a saved change to see the exact diff, then restore that version when you need it.",
    img: "/images/app/feat-atlas-activity.png",
    imgAlt: "The Story Atlas Activity timeline: today's changes to character, world, and note files, each with author and word-level change counts",
    accent: "var(--clay)",
    tall: true,
  },
  {
    id: "custom-reviewers",
    kicker: "custom reviewers",
    tag: "pro",
    title: "build your",
    titleAccent: "own editor.",
    body: "start from a template (professional proofreader, hemingway editor, conflict generator) or write your own from scratch. give it a name, a tier, instructions, and a couple of examples, and it runs alongside the built-in editors on every manuscript.",
    img: "/images/app/feat-custom-reviewer.png",
    imgAlt: "The new custom reviewer form with templates, name, tier, and instructions",
    accent: "var(--pink)",
  },
  {
    kicker: "open expression",
    tag: "pro · 18+",
    title: "set the boundary",
    titleAccent: "your story needs.",
    body: "mature-fiction support is off by default and available after age confirmation. the model picker shows which models support darker or explicit material, while Quarkle's safety boundary remains fixed across every plan.",
    img: "/images/app/feat-open-expression.png",
    imgAlt: "The model picker with models that support mature-fiction requests clearly marked",
    accent: "var(--violet)",
    tall: true,
  },
  {
    kicker: "sharing & collaboration",
    title: "share it,",
    titleAccent: "without losing control.",
    body: "invite a reader by email (they get read-only access, never edit rights) or flip on a public reading link anyone can open. turn it off any time to make the manuscript private again.",
    img: "/images/app/feat-share.png",
    imgAlt: "The Share panel with an email invite field and the private manuscript visibility toggle",
    accent: "var(--sky)",
    tall: true,
  },
  {
    kicker: "import & export",
    title: "your file,",
    titleAccent: "in and out.",
    body: "bring in pdf, markdown, or plain text. each file becomes its own chapter. export to word, pdf, or markdown with a real template designer: classic book, modern editorial, literary, or submission manuscript, with your own title page, fonts, and page size.",
    img: "/images/app/feat-export.png",
    imgAlt: "The Export Document dialog with format and design template options, showing the title page for The Weight of Water",
    accent: "var(--moss)",
  },
  {
    id: "milestones",
    kicker: "milestones",
    title: "the shape",
    titleAccent: "of your year.",
    body: "streaks, a daily average, a heatmap of your whole year. set writing days and a monthly word-count goal and quarkle works out the daily target for you. streaks reward showing up, not word-count heroics.",
    img: "/images/app/feat-milestones.png",
    imgAlt: "A year-long writing activity heatmap showing which days had writing sessions",
    accent: "var(--pink)",
  },
  {
    id: "tally",
    kicker: "the tally",
    title: "your manuscript,",
    titleAccent: "counted.",
    body: "readability, dialogue share, sentence length, stock phrases, and the words you lean on: counted from your chapters, not a model's opinion. every chapter is measured against published fiction, and the outliers get named. when chapter 2 reads denser than the rest, the tally says so, and opens it.",
    img: "/images/app/feat-tally.jpg",
    imgAlt: "The Tally: word count, readability grade, dialogue share, and stock phrases for a manuscript, with per-chapter charts against published fiction and a list of leaned-on words",
    accent: "var(--moss)",
    tall: true,
  },
  {
    id: "read-aloud",
    kicker: "read aloud",
    tag: "pro",
    title: "hear your book,",
    titleAccent: "read to you.",
    body: "the narrator reads from your cursor, marking each sentence and word as it speaks. pause it, skip a sentence, pick the voice, the language, the speed. hearing your prose out loud is the oldest editing trick there is; now it's one button.",
    img: "/images/app/feat-read-aloud.jpg",
    imgAlt: "The Read Aloud bar over a manuscript: play controls, speed, language, and voice pickers, with the word being spoken highlighted in the prose",
    accent: "var(--clay)",
  },
];

const IMAGE_DIMS = {
  "/images/app/editor-overview.png": [3288, 1934],
  "/images/app/feat-review-room.png": [2670, 1770],
  "/images/app/feat-atlas-tree.png": [1334, 1568],
  "/images/app/feat-atlas-note.png": [1332, 1656],
  "/images/app/feat-drawing-styles.jpg": [1812, 1550],
  "/images/app/feat-chat-edits.png": [3288, 1934],
  "/images/app/feat-highlight-bar.png": [1530, 900],
  "/images/app/feat-versions-wide.png": [1228, 437],
  "/images/app/feat-atlas-activity.png": [1100, 1740],
  "/images/app/feat-custom-reviewer.png": [1340, 1690],
  "/images/app/feat-open-expression.png": [855, 820],
  "/images/app/feat-share.png": [755, 895],
  "/images/app/feat-export.png": [1800, 1755],
  "/images/app/feat-milestones.png": [1470, 392],
  "/images/app/feat-report.png": [1826, 1576],
  "/images/app/feat-tally.jpg": [1080, 979],
  "/images/app/feat-read-aloud.jpg": [1080, 808],
};

function FeatureSection({ f, i }) {
  const reverse = i % 2 === 1;
  const bg = i % 2 === 0 ? "var(--paper)" : "var(--paper-2)";
  const [width, height] = f.video ? f.videoDims : IMAGE_DIMS[f.img];
  return (
    <section id={f.id} style={{ padding: "76px 40px", background: bg, borderTop: "1px solid var(--rule)", scrollMarginTop: 90 }}>
      <div
        className="qv6-feature-page-row"
        style={{
          maxWidth: 1200,
          margin: "0 auto",
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 64,
          alignItems: "center",
        }}
      >
        <div style={{ order: reverse ? 2 : 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
            <span className="caps" style={{ color: f.accent }}>{f.kicker}</span>
            {f.tag && (
              <span
                className="mono"
                style={{
                  fontSize: 9,
                  fontWeight: 700,
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                  color: "var(--paper)",
                  background: "var(--ink)",
                  borderRadius: 999,
                  padding: "3px 9px",
                }}
              >
                {f.tag}
              </span>
            )}
          </div>
          <h2
            className="tanker"
            style={{ fontSize: "clamp(36px, 4.4vw, 64px)", lineHeight: 1, margin: "10px 0 0", letterSpacing: "-0.03em" }}
          >
            {f.title}{" "}
            <span className="display" style={{ fontStyle: "italic", color: f.accent }}>{f.titleAccent}</span>
          </h2>
          <Squiggle w={110} h={10} color={f.accent} style={{ marginTop: 14 }} />
          <p className="display" style={{ fontSize: 17, fontStyle: "italic", lineHeight: 1.6, opacity: 0.85, marginTop: 18, maxWidth: 480 }}>
            {f.body}
          </p>
        </div>
        <div style={{ order: reverse ? 1 : 2, display: "flex", justifyContent: "center" }}>
          <figure style={{ margin: 0, maxWidth: f.tall ? 420 : "100%" }}>
            <div
              style={{
                borderRadius: 12,
                overflow: "hidden",
                border: "1.5px solid var(--ink)",
                boxShadow: "12px 12px 0 var(--ink)",
              }}
            >
              {f.video ? (
                <video
                  src={f.video}
                  width={width}
                  height={height}
                  autoPlay
                  loop
                  muted
                  playsInline
                  aria-label={f.imgAlt}
                  style={{ display: "block", width: "100%", height: "auto" }}
                />
              ) : (
                <Image
                  src={f.img}
                  alt={f.imgAlt}
                  width={width}
                  height={height}
                  priority={i === 0}
                  sizes="(max-width: 760px) calc(100vw - 36px), (max-width: 1400px) 50vw, 600px"
                  style={{ display: "block", width: "100%", height: "auto" }}
                />
              )}
            </div>
          </figure>
        </div>
      </div>
    </section>
  );
}

export default function FeaturesPage() {
  const [theme, setTheme] = useState("paper");
  useEffect(() => {
    try {
      const s = window.localStorage.getItem("qv6-theme");
      if (s === "paper" || s === "midnight") setTheme(s);
    } catch (_) {}
  }, []);

  return (
    <div className={`qv6 ${theme === "midnight" ? "theme-midnight" : ""}`}>
      <Nav />

      <section style={{ padding: "80px 40px 50px", textAlign: "center" }}>
        <div style={{ maxWidth: 820, margin: "0 auto" }}>
          <CornerPin color="var(--pink)">the product · feature by feature</CornerPin>
          <h1
            className="tanker"
            style={{ fontSize: "clamp(48px, 7.5vw, 112px)", lineHeight: 0.94, letterSpacing: "-0.035em", margin: "16px 0 18px" }}
          >
            an AI editorial room, <span className="display" style={{ fontStyle: "italic", color: "var(--pink)" }}>inside the draft</span>.
          </h1>
          <p className="display" style={{ fontSize: 19, fontStyle: "italic", opacity: 0.82, lineHeight: 1.55 }}>
            12 specialist AI review passes, manuscript-derived story memory, chat that edits the chapter for you, a quill that writes the next passage, AI book covers and character art, and the controls to accept, restore, share, or export. real product screenshots, no mockups.
          </p>
        </div>
      </section>

      {FEATURES.map((f, i) => (
        <FeatureSection key={i} f={f} i={i} />
      ))}

      <section style={{ padding: "90px 40px", textAlign: "center", background: "var(--navy)", color: "var(--paper-3)" }}>
        <h2 className="tanker" style={{ fontSize: "clamp(40px, 5vw, 80px)", margin: "0 0 24px", lineHeight: 0.95 }}>
          try it on your own <span className="display" style={{ fontStyle: "italic", color: "var(--pink)" }}>chapter one</span>.
        </h2>
        <StartWritingButton variant="pink" size="xl">start writing →</StartWritingButton>
      </section>

      <Footer />
    </div>
  );
}
